import { EventDispatcher } from "../application/EventDispatcher";
import { endpoint } from "../net/endpoints";
import { DtUser } from "./DtUser";
import { DtFacility } from "./DtFacility";
import { fetchFile, fetchJson } from "../net/fetch";
import { fixGroupUrn } from "./encoding/urn";
import { DT_TEAM_USERS_CHANGED_EVENT, DT_TEAM_CHANGED_EVENT, DT_TEAM_FACILITIES_CHANGED_EVENT } from "./DtEventTypes";
import { PermissionChecker } from "./PermissionChecker";
import { DtConstants } from "./schema/DtConstants";

/**
 * Represents team.
 *
 * @alias Autodesk.Tandem.DtTeam
 */
export class DtTeam {
  /**
   * Constructor
   *
   * @param {DtApp} app
   * @param {Object} team
   * @param {string} team.urn - The team's URN
   * @param {string} team.name - The team's name
   * @param {string} team.accessLevel - The user's access level for this team
   * @param {Object} [team.accountSettings] - The team's account settings
   * @param {string} team.owner - The team owner's user ID
   * @param {number} [team.thumbnailLastUpdate] - Timestamp of last thumbnail update
   * @param {string} [team.usageEmailStatus] - Status of usage email notifications
   */
  constructor(app, team) {
    this.app = app;
    this.loadContext = app.loadContext;

    this.id = fixGroupUrn(team.urn);
    this.name = team.name;
    this.accessLevel = team.accessLevel;
    this.accountSettings = team.accountSettings;
    this.owner = team.owner;

    this.users = null;
    this.facilities = null;
    this.thumbnailLastUpdate = team.thumbnailLastUpdate;
    this.usageEmailStatus = team.usageEmailStatus;
  }

  /**
   * Returns the URN of the team.
   *
   * @returns {string}
   *
   * @alias Autodesk.Tandem.DtTeam#urn
   */
  urn() {
    return this.id;
  }

  /**
   * Sets the name of the team.
   *
   * @param {string} name
   *
   * @alias Autodesk.Tandem.DtTeam#setName
   */
  async setName(name) {
    if (this.name !== name) {
      await fetchJson(this.app.loadContext, `/groups/${this.urn()}`, "PATCH", { name });
      this.name = name;
      this.app.dispatchEvent({
        type: DT_TEAM_CHANGED_EVENT,
        team: this
      });
    }
  }

  async updateThumbnail(file) {
    await fetchFile(this.app.loadContext, `/groups/${this.urn()}/thumbnail`, "PUT", file);
    this.thumbnailLastUpdate = Date.now();
    this.app.dispatchEvent({
      type: DT_TEAM_CHANGED_EVENT,
      team: this
    });
  }

  async deleteThumbnail() {
    await fetchJson(this.app.loadContext, `/groups/${this.urn()}/thumbnail`, "DELETE");
    this.thumbnailLastUpdate = null;
    this.app.dispatchEvent({
      type: DT_TEAM_CHANGED_EVENT,
      team: this
    });
  }


  get thumbnailUrl() {
    return this.thumbnailLastUpdate ? this.app.loadContext.endpoint + `/groups/${this.urn()}/thumbnail?t=${this.thumbnailLastUpdate}` : null;
  }

  // PermissionChecker impl
  _getAccessLevel() {
    return this.accessLevel;
  }

  /**
   * Returns the users of the team.
   *
   * @returns {Promise<object[]>}
   *
   * @alias Autodesk.Tandem.DtTeam#getUsers
   */
  async getUsers() {
    if (!this._usersPromise) {
      this._usersPromise = (async () => {
        const team = await fetchJson(this.app.loadContext, `/groups/${this.urn()}`);
        this._users = team.users.map((u) => new DtUser(u));
      })();
    }

    await this._usersPromise;
    return this._users;
  }

  async getExternalUsers() {
    const res = await fetchJson(this.app.loadContext, `/groups/${this.urn()}/externalUsers`);
    let twinId2externalUsers = {};
    if (!res) return twinId2externalUsers;
    for (let [uid, details] of Object.entries(res)) {
      for (let [twinId, acl] of Object.entries(details.twins)) {
        if (!(twinId in twinId2externalUsers)) {
          twinId2externalUsers[twinId] = [];
        }
        twinId2externalUsers[twinId].push(new DtUser({ id: uid, name: details.name, email: details.email, accessLevel: acl, group: this }));
      }
    }

    return twinId2externalUsers;
  }

  async updateUserAccessBulk(dtUsers) {
    await Promise.all(
      dtUsers.map(({ id, accessLevel }) =>
      fetchJson(this.app.loadContext, `/groups/${this.urn()}/users/${id}`, "PUT", { accessLevel })
      )
    );

    this._usersPromise = null;

    this.app.dispatchEvent({
      type: DT_TEAM_USERS_CHANGED_EVENT,
      team: this
    });
  }

  async updateUserAccess(dtUser) {
    return this.updateUserAccessBulk([dtUser]);
  }

  async resendInvitation(dtUser) {
    await fetchJson(this.app.loadContext, `/groups/${this.urn()}/users/${dtUser.id}/resend-email`, "POST");
  }

  // facilites
  async loadFacilities(forceRefresh = false) {
    return !forceRefresh && this.loadFacilitiesPromise || (this.loadFacilitiesPromise = this._loadFacilities(forceRefresh));
  }

  async _loadFacilities(forceRefresh = false) {
    const userResources = await this.app.loadUserResources(forceRefresh);
    const promisesPerRegion = {};
    for (const twin of userResources.twins) {
      if (this.app.supportedRegions.has(twin.region) && twin.grantedViaGroup === this.urn()) {
        if (!promisesPerRegion[twin.region]) {
          const ctx = { ...this.app.loadContext };
          endpoint.switchRegion(ctx, twin.region);
          promisesPerRegion[twin.region] = fetchJson(ctx, `/groups/${this.urn()}/twins`);
        }
      }
    }

    const results = await Promise.all(Object.values(promisesPerRegion));

    const twins = results.reduce((acc, res) => {
      return { ...acc, ...res };
    }, {});

    this.facilities = [];
    for (let twinId in twins) {
      const settings = twins[twinId];
      this.facilities.push(
        new DtFacility(twinId, this.app, settings, settings.region)
      );
    }

    this.app.dispatchEvent({
      type: DT_TEAM_FACILITIES_CHANGED_EVENT,
      team: this
    });
  }

  async getFacilities(forceRefresh = false) {
    await this.loadFacilities(forceRefresh);
    return this.facilities;
  }

  findFacility(urn) {
    if (this.facilities) {
      for (let f of this.facilities) {
        if (f.urn() === urn) {
          return f;
        }
      }
    }
    return null;
  }

  async _createFacility(path, settings, region = DtConstants.TandemRegions.US) {
    const ctx = { ...this.app.loadContext };
    endpoint.switchRegion(ctx, region);
    const res = await fetchJson(ctx, path, "POST", settings ? { settings } : null);
    const facility = new DtFacility(res.k, this.app, res.settings, region);

    if (path.includes('make-sample')) {
      // sample models are created asynchronously, and the code that waits for them to be ready expects them to be "loaded"
      // this is not great, but at least we only do that for sample facility, which is rather an edge case
      await facility.load();
    }
    if (!this.facilities) {
      this.facilities = [facility];
    } else {
      this.facilities.push(facility);
    }
    this.app.dispatchEvent({
      type: DT_TEAM_FACILITIES_CHANGED_EVENT,
      team: this
    });
    return facility;
  }

  async createSampleFacility() {
    return this._createFacility(`/groups/${this.urn()}/make-sample`);
  }

  async createFacility(settings, region = DtConstants.TandemRegions.US) {
    return this._createFacility(`/groups/${this.urn()}/twins`, settings, region);
  }

  async deleteFacility(urn) {
    const facility = this.facilities.find((f) => f.urn() === urn);

    await fetchJson(facility.loadContext, `/twins/${urn}`, 'DELETE', null);
    if (this.facilities) {
      const facilityIdx = this.facilities.findIndex((a) => a.urn() === urn);
      if (facilityIdx !== -1) {
        this.facilities.splice(facilityIdx, 1);
        this.app.dispatchEvent({
          type: DT_TEAM_FACILITIES_CHANGED_EVENT,
          team: this
        });
      }
    }
    return this.facilities;
  }

  // metrics
  async loadMetrics(start, end) {
    const params = start && end ? `?start=${start}&end=${end}` : '';

    return await fetchJson(this.app.loadContext, `/groups/${this.urn()}${params}/metrics`);
  }

  // Handover

  /**
   * Validate target account info prior twin handover
   * @param {Object} validationRequest - The required input for validation
   * @param {string} validationRequest.groupID - The id of target account
   * @param {string} validationRequest.email - The email of the account admin
   */
  async validateGroup(validationRequest) {
    const path = `/groups/${this.urn()}/validate`;
    return await fetchJson(this.app.loadContext, path, "POST", validationRequest);
  }

  /**
   * Transfers the current facility to the specified target group
   * @param {DtFacility} twin - Twin to move.
   * @param {string} targetGroupID - The group ID of where this facility will be delivered to
   * @param {boolean} revokeApiKeys - Whether to revoke all Twin-level API key permissions as part of the move.
   */
  async deliver(twin, targetGroupID, revokeApiKeys) {
    const idx = this.facilities?.indexOf(twin);
    if (idx === -1) throw new Error(`twin to deliver does not exist in account: ${twin.urn()}`);

    let path = `/twins/${twin.urn()}/move/${targetGroupID}`;
    await fetchJson(twin.loadContext, path, 'POST', { revokeApiKeys: !!revokeApiKeys });

    // Proactively updates the listed twins instead of waiting for resulting events. (One removed and receipt added)
    this.loadFacilities(true).
    catch((err) => console.error('Failed to force refresh of team twins', err));
  }

  /** Reacts to external change notifications for the active DtTeam */
  onTeamChanged(change) {
    if (change.ctype === DtConstants.ChangeTypes.UpdateGroupTwins) {

      // There are no case of group Twins updated, with no added and no removed.
      if (change.removedElements?.length && !change.addedElements?.length) {
        const twinUrns = change.removedElements.map((id) => DtConstants.DT_TWIN_URN_PREFIX + ":" + id);
        for (const urn of twinUrns) {
          const idx = this.facilities?.findIndex((f) => f.urn() === urn);
          if (idx && idx !== -1) {
            this.facilities.splice(idx, 1);
          }
        }

        this.app.dispatchEvent({
          type: DT_TEAM_FACILITIES_CHANGED_EVENT,
          team: this
        });

      } else {
        // If it's not purely a removal, reload everything.
        this.loadFacilities(true).
        catch((err) => console.error('Failed to force refresh of team twins', err));
      }
    }

    this.app.dispatchEvent({
      type: DT_TEAM_CHANGED_EVENT,
      team: this,
      change
    });
  }

  async getHistory(timestamps, min, max, includeChanges, limit) {
    const body = { timestamps, min, max, includeChanges, limit };
    return fetchJson(this.app.loadContext, `/groups/${this.urn()}/history`, 'POST', body);
  }
}

EventDispatcher.prototype.apply(DtTeam.prototype);
PermissionChecker.prototype.apply(DtTeam.prototype);
DtTeam.prototype.constructor = DtTeam;